import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react';
import { useUserState } from '@app/state/userStore';
import { useAddress, useContract } from '@thirdweb-dev/react';
import { usePosition } from '@app/hooks/v3/usePositions';
import {
  useBurnActionHandlers,
  useBurnState,
  useDerivedBurnInfo
} from '@app/state/burnStore';
import { NonfungiblePositionManager, Percent } from '@cryptoalgebra/kim-sdk';
import { algebraPositionManagerABI } from '../../../../../abis/v3';
import { useAsyncFn } from 'react-use';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import s from '@app/components/Swap/components/TokenSelector/TokenSelector.module.scss';
import { useConfig } from '@app/config';

interface Props {
  onClose: (txHash?: string) => void;
  isOpen: boolean;
  positionId: number;
}

export const RemoveLiquidityModalV3Content: FC<Props> = ({
  positionId,
  isOpen,
  onClose
}) => {
  const [sliderValue, setSliderValue] = useState(25);

  const { txDeadline } = useUserState();
  const account = useAddress();

  const { position } = usePosition(positionId);

  const { percent, burnPosition } = useBurnState();

  const { onPercentSelect, onToggleBurnPosition } = useBurnActionHandlers();

  const derivedInfo = useDerivedBurnInfo(position, false);

  const {
    position: positionSDK,
    liquidityPercentage,
    feeValue0,
    feeValue1
  } = derivedInfo;

  const { calldata, value } = useMemo(() => {
    if (
      !positionSDK ||
      !positionId ||
      !liquidityPercentage ||
      !feeValue0 ||
      !feeValue1 ||
      !account ||
      percent === 0
    ) {
      return { calldata: undefined, value: undefined };
    }

    return NonfungiblePositionManager.removeCallParameters(positionSDK, {
      tokenId: String(positionId),
      liquidityPercentage,
      slippageTolerance: new Percent(1, 100),
      deadline: Date.now() + txDeadline * 1000,
      burnToken: burnPosition,
      collectOptions: {
        expectedCurrencyOwed0: feeValue0,
        expectedCurrencyOwed1: feeValue1,
        recipient: account
      }
    });
  }, [
    positionSDK,
    positionId,
    liquidityPercentage,
    feeValue0,
    feeValue1,
    account,
    percent,
    txDeadline,
    burnPosition
  ]);
  const config = useConfig();

  const { contract } = useContract(
    config?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER,
    algebraPositionManagerABI
  );

  const [{ loading: isRemoveLoading }, removeLiquidity] =
    useAsyncFn(async () => {
      if (!contract || !calldata) {
        return;
      }

      try {
        const res = await contract.call(
          'multicall',
          calldata && [calldata as `0x${string}`[]],
          {
            value: BigInt(value || 0)
          }
        );

        if (res?.reason) {
          showErrorToast('Transaction rejected');
        }

        const txHash = res?.receipt?.transactionHash;

        if (txHash) {
          showSuccessToast(
            `Transaction successful.`,
            <ExternalLink txHash={txHash} />
          );
          onClose(txHash);
        }
      } catch (e: any) {
        console.error(e.message);

        if (e.message?.includes('user rejected transaction')) {
          return;
        }

        showErrorToast(`Transaction failed: ${e.message}`);
      }
    }, [contract, calldata]);

  const isDisabled =
    sliderValue === 0 || isRemoveLoading || !removeLiquidity || !calldata;

  useEffect(() => {
    onPercentSelect(sliderValue);
  }, [onPercentSelect, sliderValue]);

  function renderButton(label: string, value: number, isActive: boolean) {
    return (
      <Button
        py="10px"
        px="12px"
        fontFamily="'Inter', sans-serif"
        fontSize="12px"
        lineHeight="18px"
        letterSpacing="-0.18px"
        w="100%"
        color={isActive ? 'white' : 'neutral.200'}
        borderRadius="8px"
        border="0.5px solid"
        borderColor="neutral.500"
        bg={isActive ? 'neutral.500' : 'neutral.700'}
        transition="color 0.2s ease"
        _hover={{ color: 'white' }}
        onClick={() => {
          if (value !== 100) {
            onToggleBurnPosition(false);
          }

          setSliderValue(value);
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <ModalHeader>
          <HStack justify="space-between" px={0} py={0} mb="16px">
            <Text fontSize="16px" color="white" fontWeight="500">
              Withdraw liquidity
            </Text>
            <ModalCloseButton onClick={() => onClose()} />
          </HStack>
        </ModalHeader>
        <VStack w="100%">
          <Box w="100%">
            <Text
              textAlign="left"
              w="100%"
              color="neutral.300"
              fontSize="12px"
              lineHeight="18px"
              letterSpacing="-0.18px"
              mb="4px"
            >
              Amount
            </Text>
            <Grid gridTemplateColumns="70px 40px" alignItems="center">
              <DebouncedInput
                textAlign="right"
                type="text"
                amount={percent.toString()}
                readOnlyValue={percent.toString()}
              />
              <Text
                fontSize="24px"
                fontWeight="400"
                lineHeight="1"
                letterSpacing="-0.96px"
                color="neutral.400"
              >
                %
              </Text>
            </Grid>
            <HStack w="100%" mt="8px">
              {renderButton('25%', 25, percent === 25)}
              {renderButton('50%', 50, percent === 50)}
              {renderButton('75%', 75, percent === 75)}
              {renderButton('100%', 100, percent === 100)}
            </HStack>
          </Box>

          <Box
            borderTop="1px solid"
            borderColor="whiteOpacity.6"
            py="24px"
            w="100%"
          >
            <Text fontSize={12} color="neutral.400" pb={3}>
              Toggle this checkbox on if you want to burn your position and
              remove it from your positions list or leave it empty to be able to
              add liquidity later
            </Text>
            <Checkbox
              isDisabled={percent !== 100}
              // defaultChecked={burnPosition}
              size="md"
              colorScheme="green"
              className={s.checkbox}
              isChecked={burnPosition}
              onChange={() => onToggleBurnPosition(!burnPosition)}
            >
              Burn position
            </Checkbox>
          </Box>
          <SubmitButton
            mt={0}
            label="Remove Liquidity"
            isLoading={isRemoveLoading}
            disabled={isDisabled}
            onClick={() => removeLiquidity && removeLiquidity()}
          />
        </VStack>
      </ModalContent>
    </Modal>
  );
};
